import { useTranslation } from "react-i18next";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import BiotechOutlinedIcon from "@mui/icons-material/BiotechOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";

// Semantic verdict colors (meaningful, not decorative — the S/I/R traffic-light
// convention). Everything else follows the site's MUI theme (blue primary, Roboto).
const VERDICTS = [
  { code: "S", color: "#2e7d32", nameKey: "about.how.sName", bodyKey: "about.how.sBody" },
  { code: "I", color: "#ed6c02", nameKey: "about.how.iName", bodyKey: "about.how.iBody" },
  { code: "R", color: "#c62828", nameKey: "about.how.rName", bodyKey: "about.how.rBody" },
];
const HOW_STEPS = [
  { icon: PhotoCameraOutlinedIcon, titleKey: "about.how.captureTitle", bodyKey: "about.how.captureBody" },
  { icon: BiotechOutlinedIcon, titleKey: "about.how.interpretTitle", bodyKey: "about.how.interpretBody" },
  { icon: AssignmentOutlinedIcon, titleKey: "about.how.recordTitle", bodyKey: "about.how.recordBody" },
];
const PILLAR_ICONS = [
  FactCheckOutlinedIcon,
  DevicesOutlinedIcon,
  VerifiedOutlinedIcon,
  LockOutlinedIcon,
  CloudOffOutlinedIcon,
];

function Section({ id, children, divider = true }) {
  return (
    <Box
      component="section"
      id={id}
      sx={{
        py: { xs: 5, md: 7 },
        ...(divider && { borderTop: 1, borderColor: "divider" }),
      }}
    >
      {children}
    </Box>
  );
}

function About() {
  const { t } = useTranslation();
  const audienceCards = t("about.who.cards", { returnObjects: true }) || [];
  const pillars = t("about.trust.pillars", { returnObjects: true }) || [];

  return (
    <Container maxWidth="lg" sx={{ pb: 6 }}>
      {/* HERO */}
      <Box component="section" sx={{ py: { xs: 4, md: 6 } }}>
        <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
          <Grid size={{ xs: 12, md: 7 }}>
            <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
              {t("about.hero.eyebrow")}
            </Typography>
            <Typography
              variant="h3"
              component="h1"
              sx={{ fontWeight: 700, letterSpacing: "-0.01em", lineHeight: 1.15, mb: 2 }}
            >
              {t("about.hero.headline")}
            </Typography>
            <Typography variant="h6" component="p" color="text.secondary" sx={{ fontWeight: 400, mb: 2 }}>
              {t("about.hero.subhead")}
            </Typography>
            <Box sx={{ mb: 3 }}>
              {["about.p1", "about.p2"].map((p) => (
                <Typography key={p} variant="body1" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
                  {t(p)}
                </Typography>
              ))}
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
              {[t("about.hero.chip1"), t("about.hero.chip2")].map((label) => (
                <Chip key={label} icon={<CheckCircleOutlineIcon />} label={label} color="primary" variant="outlined" />
              ))}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Box
              component="img"
              src="/assets/images/about-plate.svg"
              alt={t("about.hero.eyebrow")}
              sx={{
                width: "100%",
                maxWidth: 420,
                display: "block",
                mx: "auto",
                filter: "drop-shadow(0 14px 32px rgba(6,30,28,0.16))",
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* BASICS */}
      <Section id="about-basics">
        <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 1.5 }}>
          {t("about.basics.heading")}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t("about.basics.intro")}
        </Typography>

        <Paper elevation={2} sx={{ p: { xs: 1, sm: 2 } }}>
          {[
            { titleKey: "about.basics.amrTitle", paras: ["about.basics.amrP1", "about.basics.amrP2"] },
            { titleKey: "about.basics.astTitle", paras: ["about.basics.astP1", "about.basics.astP2"] },
          ].map((panel) => (
            <Accordion key={panel.titleKey} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 600 }}>{t(panel.titleKey)}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {panel.paras.map((p) => (
                  <Typography key={p} variant="body1" component="p" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.75 }}>
                    {t(p)}
                  </Typography>
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        </Paper>
      </Section>

      {/* HOW IT WORKS */}
      <Section id="about-how">
        <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 1.5 }}>
          {t("about.how.heading")}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {t("about.how.intro")}
        </Typography>

        <Grid container spacing={2.5}>
          {HOW_STEPS.map((step, i) => {
            const Icon = step.icon;
            return (
              <Grid size={{ xs: 12, md: 4 }} key={step.titleKey}>
                <Paper elevation={2} sx={{ p: 3, height: "100%" }}>
                  <Typography variant="overline" color="text.secondary">
                    {`Step ${String(i + 1).padStart(2, "0")}`}
                  </Typography>
                  <Icon color="primary" sx={{ fontSize: 32, display: "block", my: 1 }} />
                  <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {t(step.titleKey)}
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {t(step.bodyKey)}
                  </Typography>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mt: 5, mb: 2.5 }}>
          {t("about.how.verdictIntro")}
        </Typography>
        <Grid container spacing={2.5}>
          {VERDICTS.map((v) => (
            <Grid size={{ xs: 12, sm: 4 }} key={v.code}>
              <Paper elevation={2} sx={{ p: 2.5, height: "100%", borderTop: `4px solid ${v.color}` }}>
                <Typography sx={{ color: v.color, fontWeight: 800, fontSize: "1.9rem", lineHeight: 1, fontFamily: "monospace" }}>
                  {v.code}
                </Typography>
                <Typography sx={{ fontWeight: 700, mt: 1, mb: 0.5 }}>{t(v.nameKey)}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  {t(v.bodyKey)}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2.5 }}>
          {t("about.how.verdictCaption")}
        </Typography>
      </Section>

      {/* WHO IT'S FOR */}
      <Section id="about-who">
        <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 1.5 }}>
          {t("about.who.heading")}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {t("about.who.intro")}
        </Typography>

        <Grid container spacing={2.5}>
          {audienceCards.map((card) => (
            <Grid size={{ xs: 12, md: 4 }} key={card.role}>
              <Paper elevation={2} sx={{ p: 3, height: "100%" }}>
                <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700, mb: 1 }}>
                  {card.role}
                </Typography>
                <Typography variant="h6" component="h3" sx={{ fontWeight: 700, mb: 2 }}>
                  {card.question}
                </Typography>
                <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
                  {(card.bullets || []).map((b) => (
                    <Box
                      component="li"
                      key={b.lead}
                      sx={{
                        position: "relative",
                        pl: 2.5,
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: "0.55em",
                          width: 7,
                          height: 7,
                          borderRadius: "2px",
                          bgcolor: "primary.main",
                        },
                      }}
                    >
                      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                        <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
                          {b.lead}
                        </Box>{" "}
                        {b.body}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* TRUST / ANNEX */}
      <Section id="about-trust">
        <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 1.5 }}>
          {t("about.trust.heading")}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {t("about.trust.intro")}
        </Typography>

        <Grid container spacing={3}>
          {pillars.map((pillar, i) => {
            const Icon = PILLAR_ICONS[i] || VerifiedOutlinedIcon;
            return (
              <Grid size={{ xs: 12, md: 6 }} key={pillar.title}>
                <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      border: 1,
                      borderColor: "divider",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "primary.main",
                    }}
                  >
                    <Icon />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{pillar.title}</Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {pillar.body}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Section>

      {/* CLOSE */}
      <Section id="about-close">
        <Typography variant="h4" component="h2" sx={{ fontWeight: 700, mb: 2, maxWidth: "20ch" }}>
          {t("about.close.heading")}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: "1.1rem", lineHeight: 1.8 }}>
          {t("about.close.body")}
        </Typography>
        <Divider sx={{ mt: 6 }} />
      </Section>
    </Container>
  );
}

export default About;
